<template>
  <div class="modal-content">
    <div class="modal-header">
      <h4 class="modal-title">{{ _('Pick location from the map') }}</h4>
    </div>
    <div class="modal-body">
      <div class="row">
        <div class="col-xs-12">
          <div class="form-group location-search">
            <div class="input-group">
              <input
                type="text"
                class="form-control"
                v-model="address"
                autocomplete="off"
                v-on:keydown.enter.prevent="lookupCoordinates"
              />
              <div class="input-group-btn">
                <button class="btn btn-danger" type="button" v-on:click="clearAddress"><i class="fa fa-fw fa-times"></i></button>
              </div>
              <div class="input-group-btn">
                <button class="btn btn-info" type="button" v-on:click="lookupCoordinates"><i class="fa fa-fw fa-search"></i> {{ _('Search') }}</button>
              </div>
            </div>
            <ul v-if="suggestions.length" class="location-suggest">
              <li v-for="item in suggestions" :key="item.key">
                <button type="button" v-on:click="applySuggestion(item)">{{ item.label }}</button>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <Basemap
        ref="basemap"
        class="location-picker-map"
        :center="mapCenter"
        :zoom="mapZoom"
        :markers="mapMarkers"
        :fit-markers="false"
        :pick="true"
        :lang="locale"
        :tile-url="mapsBasemapUrl"
        @pick="onMapPick"
        @marker-drag="onMarkerDrag"
      />
    </div>
    <div class="modal-footer">
      <button class="btn btn-default" v-on:click="hideModal"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
      <button class="btn btn-success" v-on:click="selectLayout"><i class="fa fa-fw fa-save"></i> {{ _('OK') }}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import { useRuntimeConfig } from '#imports'
import Basemap from '../Basemap.vue'
import { DEFAULT_MAP_CENTER, DEFAULT_MAPS_BASEMAP_URL } from '../../lib/basemapStyle'

export default {
  components: { Basemap },
  props: ['defaultLat', 'defaultLng', 'defaultAddress', 'settings', 'locale'],
  setup () {
    const config = useRuntimeConfig()
    return {
      mapsBasemapUrl: config.public?.mapsBasemapUrl || DEFAULT_MAPS_BASEMAP_URL
    }
  },
  data () {
    const lat = parseFloat(this.defaultLat)
    const lng = parseFloat(this.defaultLng)
    return {
      position: {
        lat: lat || DEFAULT_MAP_CENTER.lat,
        lng: lng || DEFAULT_MAP_CENTER.lng
      },
      address: this.defaultAddress ? this.defaultAddress.trim() : '',
      mapZoom: lat && lng ? 13 : 6,
      hasMarker: !!(lat && lng),
      suggestions: [],
      suggestTimer: null,
      suppressSuggest: false
    }
  },
  computed: {
    ...mapGetters(['shopId', 'baseDomain', 'apiKey']),
    lat () {
      return this.position ? this.position.lat : 0
    },
    lng () {
      return this.position ? this.position.lng : 0
    },
    mapCenter () {
      return this.position || DEFAULT_MAP_CENTER
    },
    mapMarkers () {
      if (!this.hasMarker || !this.position) {
        return []
      }
      return [{ id: 'picked', lat: this.position.lat, lng: this.position.lng }]
    }
  },
  watch: {
    address (value) {
      if (this.suppressSuggest) {
        this.suppressSuggest = false
        return
      }
      this.queueSuggest(value)
    }
  },
  methods: {
    hideModal () {
      this.$emit('close')
    },
    selectLayout () {
      this.$emit('selectLocation', {
        lat: this.lat,
        lng: this.lng
      })
    },
    geocodeUrl (path, params) {
      const query = new URLSearchParams({ apikey: this.apiKey, ...params })
      return this.baseDomain + this.shopId + path + '?' + query.toString()
    },
    setPosition (lat, lng, zoom) {
      this.position = { lat, lng }
      this.hasMarker = true
      if (zoom) {
        this.mapZoom = zoom
      }
      this.$refs.basemap?.flyTo({ lat, lng, zoom: this.mapZoom })
    },
    onMapPick (data) {
      this.setPosition(data.lat, data.lng)
      this.lookupAddress()
    },
    onMarkerDrag (data) {
      this.position = { lat: data.lat, lng: data.lng }
      this.lookupAddress()
    },
    clearAddress () {
      this.address = ''
      this.suggestions = []
    },
    setAddress (value) {
      this.suppressSuggest = true
      this.address = value || ''
      this.suggestions = []
    },
    async lookupCoordinates () {
      const q = (this.address || '').trim()
      if (!q) {
        return
      }
      this.suggestions = []
      try {
        const { data } = await axios.get(this.geocodeUrl('/geocode.json', { q }))
        const hit = data?.results?.[0]
        if (hit?.lat && hit?.lng) {
          this.setPosition(hit.lat, hit.lng, 17)
          if (hit.label) {
            this.setAddress(hit.label)
          }
        }
      } catch (e) {
      }
    },
    async lookupAddress () {
      if (!this.position) {
        return
      }
      try {
        const { data } = await axios.get(this.geocodeUrl('/geocode/reverse.json', {
          lat: this.position.lat,
          lng: this.position.lng
        }))
        if (data?.label) {
          this.setAddress(data.label)
        }
      } catch (e) {
      }
    },
    queueSuggest (value) {
      clearTimeout(this.suggestTimer)
      const q = (value || '').trim()
      if (q.length < 2) {
        this.suggestions = []
        return
      }
      this.suggestTimer = setTimeout(() => this.lookupSuggest(q), 250)
    },
    houseNumberFrom (value) {
      const tokens = (value || '').replace(/[,;]+/g, ' ').trim().split(/\s+/)
      const hit = tokens.find(token => /^\d+[a-zA-Z]?(?:-\d+[a-zA-Z]?)?$/.test(token) && !/^\d{5}$/.test(token))
      return hit || ''
    },
    houseLabel (row) {
      if (row.label && row.housenumber && row.label.indexOf(row.housenumber) !== -1) {
        return row.label
      }
      const street = [row.street, row.housenumber].filter(Boolean).join(' ')
      const area = [row.postcode, row.city].filter(Boolean).join(' ')
      return [street, area].filter(Boolean).join(', ')
    },
    async lookupSuggest (q) {
      try {
        const { data } = await axios.get(this.geocodeUrl('/geocode/suggest.json', { q }))
        const hn = this.houseNumberFrom(q)
        const houses = (data?.houses || []).map((row, i) => ({
          key: 'house-' + i + '-' + row.street + '-' + row.housenumber + '-' + row.city,
          kind: 'house',
          label: this.houseLabel(row),
          lat: row.lat,
          lng: row.lng,
          zoom: 17
        }))
        const streets = (data?.streets || []).map((row, i) => ({
          key: 'street-' + i + '-' + row.street + '-' + row.city,
          kind: 'street',
          street: row.street,
          city: row.city,
          label: hn ? [row.street, hn].join(' ') + ', ' + row.city : row.label,
          lat: row.lat,
          lng: row.lng,
          zoom: 15
        }))
        const cities = hn
          ? []
          : (data?.cities || []).map((row, i) => ({
            key: 'city-' + i + '-' + row.name,
            kind: 'city',
            label: row.label || row.name,
            lat: row.lat,
            lng: row.lng,
            zoom: 12
          }))
        this.suggestions = (houses.length ? houses : streets.concat(cities)).slice(0, 8)
      } catch (e) {
        this.suggestions = []
      }
    },
    applySuggestion (item) {
      const hn = this.houseNumberFrom(this.address)
      if (item.kind === 'street' && hn) {
        this.setAddress([item.street, hn].join(' ') + (item.city ? ', ' + item.city : ''))
        this.lookupCoordinates()
        return
      }
      this.setAddress(item.label)
      this.setPosition(item.lat, item.lng, item.zoom)
    },
    resizeMap () {
      this.$refs.basemap?.resize()
    }
  },
  async mounted () {
    this.$nextTick(() => {
      this.resizeMap()
      setTimeout(() => this.resizeMap(), 300)
    })
    if (parseFloat(this.defaultLat) && parseFloat(this.defaultLng)) {
      if (!this.address) {
        this.lookupAddress()
      }
    } else if (this.address) {
      this.lookupCoordinates()
    }
  },
  beforeUnmount () {
    clearTimeout(this.suggestTimer)
  }
}
</script>

<style scoped>
.location-picker-map {
  width: 100%;
  height: 400px;
  min-height: 400px;
}
.location-search {
  position: relative;
}
.location-suggest {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  margin: 0;
  padding: 0;
  list-style: none;
  background: #fff;
  border: 1px solid #ccc;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}
.location-suggest button {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  text-align: left;
}
.location-suggest button:hover,
.location-suggest button:focus {
  background: #f5f5f5;
}
</style>
